import { presentLexError } from '@/lib/user-notice';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';

// Evita conflicto de tipos ReactNode entre @types/react del monorepo (TS 2786)
const Link = RouterLink as React.ComponentType<{ to: string; style?: React.CSSProperties; children?: React.ReactNode; className?: string }>;
import { getArticleById, getLawById, getArticlesByLawId } from '@/lib/laws';
import type { Article as ArticleType } from '@/types';
import {
  getAllHighlights,
  createHighlight,
  deleteHighlight,
  type Highlight,
} from '@/lib/highlights';

const contentContainerStyle: React.CSSProperties = {
  lineHeight: 1.85,
  color: 'var(--lex-on-surface)',
  fontSize: 15,
};

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Inyecta <mark> en el HTML para los resaltados de tipo highlight */
function contentWithHighlights(html: string, highlights: Highlight[]): string {
  const textHighlights = highlights.filter((h) => h.type === 'highlight' && h.text.trim());
  let result = html;
  for (const h of textHighlights) {
    const escaped = escapeRegex(h.text.trim());
    if (!escaped) continue;
    const replacement = `<mark data-highlight-id="${h.id}" class="article-highlight">${h.text}</mark>`;
    result = result.replace(new RegExp(escaped, 'g'), replacement);
  }
  return result;
}

export function ArticlePage() {
  const { lawId, articleId } = useParams<{ lawId: string; articleId: string }>();
  const [article, setArticle] = useState<Awaited<ReturnType<typeof getArticleById>>>(null);
  const [lawTitle, setLawTitle] = useState<string>('');
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [showResaltarBar, setShowResaltarBar] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  /** Texto fijado al abrir el modal para no perderlo si el clic colapsa la selección (móvil) */
  const [highlightTextInModal, setHighlightTextInModal] = useState('');
  const [createNote, setCreateNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [contextMarkId, setContextMarkId] = useState<string | null>(null);
  const [articlesList, setArticlesList] = useState<ArticleType[]>([]);
  const [showArticleIndex, setShowArticleIndex] = useState(false);
  const [indexSearch, setIndexSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const [fontSize, setFontSize] = useState(18);
  const contentRef = useRef<HTMLDivElement>(null);
  /** En móvil la selección puede colapsar antes del mouseup; guardamos la última selección válida. */
  const lastSelectionTextRef = useRef('');
  /** Evitar que el mismo toque que abre el modal cierre el overlay al soltar. */
  const modalOpenedAtRef = useRef(0);
  const navigate = useNavigate();

  const currentIndex = articlesList.findIndex((a) => a.id === articleId);
  const prevArticle = currentIndex > 0 ? articlesList[currentIndex - 1] : null;
  const nextArticle = currentIndex >= 0 && currentIndex < articlesList.length - 1 ? articlesList[currentIndex + 1] : null;

  const indexFiltered = indexSearch.trim()
    ? articlesList.filter(
        (a) =>
          a.number.toLowerCase().includes(indexSearch.toLowerCase()) ||
          (a.title && a.title.toLowerCase().includes(indexSearch.toLowerCase())) ||
          a.content.replace(/<[^>]+>/g, ' ').toLowerCase().includes(indexSearch.toLowerCase())
      )
    : articlesList;
  const INDEX_COLORS = ['#2E7D32', '#1976D2', '#E65100', '#6A1B9A', '#C2185B'];

  const loadArticle = useCallback(async () => {
    if (!lawId || !articleId) return;
    setError('');
    try {
      const [art, title] = await Promise.all([
        getArticleById(lawId, articleId),
        getLawById(lawId).then((l) => l.title),
      ]);
      setArticle(art);
      setLawTitle(title);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar el artículo.');
    }
  }, [lawId, articleId]);

  const loadHighlights = useCallback(async () => {
    if (!articleId) return;
    try {
      const list = await getAllHighlights({ articleId });
      setHighlights(list);
    } catch {
      setHighlights([]);
    }
  }, [articleId]);

  const loadArticlesForLaw = useCallback(async () => {
    if (!lawId) return;
    try {
      const list = await getArticlesByLawId(lawId);
      setArticlesList(list);
    } catch {
      setArticlesList([]);
    }
  }, [lawId]);

  useEffect(() => {
    if (!lawId || !articleId) return;
    setLoading(true);
    loadArticle()
      .then(() => Promise.all([loadHighlights(), loadArticlesForLaw()]))
      .finally(() => setLoading(false));
  }, [lawId, articleId, loadArticle, loadHighlights, loadArticlesForLaw]);

  const favorite = highlights.find((h) => h.type === 'favorite');
  const isFavorite = !!favorite;

  const handleToggleFavorite = async () => {
    if (!article || !lawId || !articleId) return;
    try {
      if (favorite) {
        await deleteHighlight(favorite.id);
        setHighlights((prev) => prev.filter((h) => h.id !== favorite.id));
      } else {
        const created = await createHighlight({
          articleId,
          lawId,
          text: article.title || `Artículo ${article.number}`,
          type: 'favorite',
          articleNumber: article.number,
          articleTitle: article.title,
          articleContent: article.content,
        });
        setHighlights((prev) => [...prev, created]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al actualizar favorito.');
    }
  };

  const applySelectionToResaltarBar = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || !contentRef.current || !contentRef.current.contains(sel.anchorNode)) {
      setShowResaltarBar(false);
      setSelectedText('');
      setContextMarkId(null);
      return;
    }
    const text = sel.toString().trim();
    const anchor = sel.anchorNode;
    const target = anchor?.parentElement?.closest?.('mark[data-highlight-id]');
    if (target) {
      setContextMarkId((target as HTMLElement).dataset.highlightId ?? null);
      setShowResaltarBar(false);
      setSelectedText('');
      return;
    }
    if (text.length >= 3 && /[a-zA-ZáéíóúÁÉÍÓÚñÑ]/.test(text)) {
      lastSelectionTextRef.current = text;
      setSelectedText(text);
      setShowResaltarBar(true);
      setContextMarkId(null);
    } else {
      const fallback = lastSelectionTextRef.current;
      if (fallback.length >= 3 && /[a-zA-ZáéíóúÁÉÍÓÚñÑ]/.test(fallback)) {
        setSelectedText(fallback);
        setShowResaltarBar(true);
        setContextMarkId(null);
      } else {
        setShowResaltarBar(false);
        setSelectedText('');
      }
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    applySelectionToResaltarBar();
  }, [applySelectionToResaltarBar]);

  /** En Safari iOS la selección es por touch; touchend dispara y la selección puede no estar lista hasta el siguiente tick. */
  const handleTouchEnd = useCallback(() => {
    setTimeout(applySelectionToResaltarBar, 50);
  }, [applySelectionToResaltarBar]);

  useEffect(() => {
    const onSelectionChange = () => {
      if (!contentRef.current) return;
      const sel = window.getSelection();
      if (!sel || sel.anchorNode == null || !contentRef.current.contains(sel.anchorNode)) return;
      const text = sel.toString().trim();
      if (text.length >= 3 && /[a-zA-ZáéíóúÁÉÍÓÚñÑ]/.test(text)) {
        if (text.length >= lastSelectionTextRef.current.length) lastSelectionTextRef.current = text;
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  // Con el modal abierto, quitar cualquier selección para que Safari no muestre el menú nativo
  useEffect(() => {
    if (!showCreateModal) return;
    const clear = () => window.getSelection()?.removeAllRanges();
    clear();
    const t = setTimeout(clear, 100);
    return () => clearTimeout(t);
  }, [showCreateModal]);

  const closeCreateModal = () => {
    if (creating) return;
    setShowCreateModal(false);
    lastSelectionTextRef.current = '';
  };

  const handleOverlayClick = () => {
    if (creating) return;
    if (Date.now() - modalOpenedAtRef.current < 400) return;
    closeCreateModal();
  };

  const openCreateModal = () => {
    modalOpenedAtRef.current = Date.now();
    const live = (window.getSelection()?.toString() ?? '').trim();
    const fromState = selectedText.trim();
    const fromRef = lastSelectionTextRef.current.trim();
    const candidates = [live, fromState, fromRef].filter((t) => t.length >= 3 && /[a-zA-ZáéíóúÁÉÍÓÚñÑ]/.test(t));
    const textToSave = candidates.length ? candidates.reduce((a, b) => (a.length >= b.length ? a : b), '') : fromState || fromRef;
    if (!textToSave || textToSave.length < 3) return;
    setHighlightTextInModal(textToSave);
    setCreateNote('');
    setShowCreateModal(true);
    setShowResaltarBar(false);
    // Quitar la selección del documento para que Safari no muestre el menú nativo (Copiar, Buscar) encima del modal
    window.getSelection()?.removeAllRanges();
  };

  const handleCreateHighlight = async () => {
    const text = highlightTextInModal.trim();
    if (!article || !lawId || !articleId || !text) return;
    setCreating(true);
    try {
      const created = await createHighlight({
        articleId,
        lawId,
        text,
        note: createNote.trim() || undefined,
        type: 'highlight',
        articleNumber: article.number,
        articleTitle: article.title,
        articleContent: article.content,
      });
      setHighlights((prev) => [...prev, created]);
      setShowCreateModal(false);
      setHighlightTextInModal('');
      setSelectedText('');
      setCreateNote('');
      lastSelectionTextRef.current = '';
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al crear resaltado.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteMark = async (id: string) => {
    try {
      await deleteHighlight(id);
      setHighlights((prev) => prev.filter((h) => h.id !== id));
      setContextMarkId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al eliminar.');
    }
  };

  const handleCopy = useCallback(async () => {
    if (!article || !lawTitle) return;
    const articleHighlights = highlights.filter((h) => h.articleId === articleId && h.type === 'highlight');
    let contentWithBold = article.content;
    for (const h of articleHighlights) {
      const escaped = h.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      contentWithBold = contentWithBold.replace(new RegExp(escaped, 'g'), `<b>${h.text}</b>`);
    }
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = contentWithBold;
    const plainContent = tempDiv.innerText || article.content.replace(/<[^>]+>/g, ' ');
    const footer = '\n\n- Obtenido de DU+ de www.dumas.solutions';
    const plainTextToCopy = `${lawTitle} - Artículo ${article.number}\n\n${plainContent}${footer}`;
    const htmlToCopy = `<h3>${lawTitle} - Artículo ${article.number}</h3><div>${contentWithBold}</div><p><br><i>- Obtenido de DU+ de <a href="https://www.dumas.solutions">www.dumas.solutions</a></i></p>`;
    try {
      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([plainTextToCopy], { type: 'text/plain' }),
        'text/html': new Blob([htmlToCopy], { type: 'text/html' }),
      });
      await navigator.clipboard.write([clipboardItem]);
    } catch {
      try {
        await navigator.clipboard.writeText(plainTextToCopy);
      } catch {
        // ignore
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [article, lawTitle, highlights, articleId]);

  if (!lawId || !articleId) {
    return (
      <main className="lex-page">
        <section className="lex-surface-card lex-card">
          <p style={{ color: 'var(--lex-danger)', margin: 0 }}>Faltan ley o artículo.</p>
        </section>
      </main>
    );
  }

  const contentHtml =
    article && highlights.length > 0
      ? contentWithHighlights(article.content, highlights)
      : article?.content ?? '';

  const iconBtnStyle: React.CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: 14,
    border: '1px solid var(--lex-outline)',
    background: 'var(--lex-surface)',
    color: 'var(--lex-primary)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
  };

  return (
    <main className="lex-page" style={{ position: 'relative', paddingBottom: 90 }}>
      <div className="lex-stack">
        <div className="lex-section-kicker" style={{ marginBottom: -2 }}>
          <Link
            to={`/leyes/${lawId}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'inherit', textDecoration: 'none' }}
          >
            <i className="ri-arrow-left-line" aria-hidden />
            Volver al detalle de ley
          </Link>
        </div>

      {error ? (
        <section className="lex-surface-card lex-card-tight">
          <p style={{ color: 'var(--lex-danger)', fontSize: 14, margin: 0 }}>{presentLexError(error)}</p>
        </section>
      ) : null}
      {loading ? (
        <section className="lex-surface-card lex-empty">
          <div className="lex-empty-icon">
            <i className="ri-loader-4-line" aria-hidden />
          </div>
          <p style={{ margin: 0 }}>Cargando artículo…</p>
        </section>
      ) : !article ? (
        <section className="lex-surface-card lex-empty">
          <p style={{ margin: 0 }}>Artículo no encontrado.</p>
        </section>
      ) : (
        <>
          <div
            className="lex-surface-card lex-card"
            style={{ marginBottom: 0 }}
          >
            <div className="lex-section-kicker" style={{ marginBottom: 8 }}>
              <i className="ri-file-text-line" aria-hidden />
              Lectura legal
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.05em', color: 'var(--lex-on-surface)' }}>
                  Artículo {article.number}
                </h1>
                {article.title ? (
                  <p style={{ margin: '8px 0 0', fontSize: 15, color: 'var(--lex-on-surface-variant)', lineHeight: 1.45 }}>
                    {article.title}
                  </p>
                ) : null}
                <div className="lex-chip-row" style={{ marginTop: 12 }}>
                  <span className="lex-chip">
                    {lawTitle}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {articlesList.length > 0 && (
                  <button type="button" onClick={() => setShowArticleIndex(true)} style={iconBtnStyle} title="Índice" aria-label="Índice">
                    <i className="ri-list-check" aria-hidden />
                  </button>
                )}
                <button type="button" onClick={handleToggleFavorite} style={iconBtnStyle} title={isFavorite ? 'Quitar favorito' : 'Marcar favorito'} aria-label="Favorito">
                  <i className={isFavorite ? 'ri-star-fill' : 'ri-star-line'} aria-hidden />
                </button>
                <button type="button" onClick={handleCopy} style={iconBtnStyle} title={copied ? 'Copiado' : 'Copiar'} aria-label="Copiar">
                  <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden />
                </button>
              </div>
            </div>
          </div>

          <section className="lex-surface-card lex-card">
            <div className="lex-section-kicker" style={{ marginBottom: 8 }}>
              Tip de lectura
            </div>
            <p style={{ fontSize: 13, color: 'var(--lex-on-surface-variant)', margin: 0 }}>
              Selecciona texto y pulsa «Resaltar» para guardar un fragmento con contexto.
            </p>
          </section>

          <section className="lex-surface-card lex-card" style={{ paddingTop: 22, paddingBottom: 28 }}>
          <div
            ref={contentRef}
            className="article-content"
            onMouseUp={handleMouseUp}
            onTouchEnd={handleTouchEnd}
            onContextMenu={(e) => e.preventDefault()}
            dangerouslySetInnerHTML={{ __html: contentHtml }}
            style={{
              ...contentContainerStyle,
              fontSize,
              padding: 0,
              WebkitTouchCallout: 'none',
            }}
          />
          </section>

          <div className="lex-reading-bar">
            {prevArticle ? (
              <button
                type="button"
                onClick={() => navigate(`/leyes/${lawId}/articulo/${prevArticle.id}`)}
                className="lex-reading-bar-action"
              >
                <i className="ri-arrow-left-s-line" style={{ fontSize: 18 }} aria-hidden />
                Anterior
              </button>
            ) : (
              <span style={{ width: 80 }} />
            )}
            <div className="lex-reading-size">
              <button
                type="button"
                onClick={() => setFontSize((s) => Math.max(12, s - 2))}
                className="lex-reading-size-button"
                aria-label="Reducir tamaño"
              >
                −
              </button>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--lex-on-surface-variant)', minWidth: 70, textAlign: 'center' }}>
                TAMAÑO {fontSize}
              </span>
              <button
                type="button"
                onClick={() => setFontSize((s) => Math.min(28, s + 2))}
                className="lex-reading-size-button"
                aria-label="Aumentar tamaño"
              >
                +
              </button>
            </div>
            {nextArticle ? (
              <button
                type="button"
                onClick={() => navigate(`/leyes/${lawId}/articulo/${nextArticle.id}`)}
                className="lex-reading-bar-action"
              >
                Siguiente
                <i className="ri-arrow-right-s-line" style={{ fontSize: 18 }} aria-hidden />
              </button>
            ) : (
              <span style={{ width: 80 }} />
            )}
          </div>

          {showResaltarBar && selectedText && (
            <div className="lex-floating-action" style={{ bottom: 80, display: 'flex', gap: 8, zIndex: 9999 }}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  openCreateModal();
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  openCreateModal();
                }}
                style={{
                  padding: '8px 16px',
                  background: 'var(--lex-primary)',
                  color: 'var(--lex-on-primary)',
                  border: 'none',
                  borderRadius: 12,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Resaltar
              </button>
            </div>
          )}

          {contextMarkId && (
            <div className="lex-floating-action" style={{ bottom: 80 }}>
              <button
                type="button"
                onClick={() => handleDeleteMark(contextMarkId)}
                style={{
                  padding: '8px 16px',
                  background: '#ffebee',
                  color: '#c62828',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Eliminar resaltado
              </button>
            </div>
          )}

          {showCreateModal && (
            <div
              className="lex-overlay lex-overlay-center"
              onClick={handleOverlayClick}
              onContextMenu={(e) => e.preventDefault()}
            >
              <div
                className="lex-sheet lex-sheet-center"
                style={{ WebkitTouchCallout: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
                onClick={(e) => e.stopPropagation()}
                onContextMenu={(e) => e.preventDefault()}
              >
                <div className="lex-section-kicker" style={{ marginBottom: 8 }}>Nuevo guardado</div>
                <h3 style={{ margin: '0 0 12px', fontSize: 20 }}>Crear resaltado</h3>
                <div className="lex-sheet-highlight" style={{ WebkitTouchCallout: 'none' }}>
                  "{highlightTextInModal.slice(0, 200)}
                  {highlightTextInModal.length > 200 ? '…' : ''}"
                </div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Nota (opcional)
                </label>
                <input
                  type="text"
                  value={createNote}
                  onChange={(e) => setCreateNote(e.target.value)}
                  placeholder="Añade una nota..."
                  className="lex-input"
                  style={{ marginBottom: 16 }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={closeCreateModal}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(255,255,255,0.72)',
                      border: '1px solid var(--lex-outline)',
                      borderRadius: 12,
                      cursor: 'pointer',
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateHighlight}
                    disabled={creating}
                    style={{
                      padding: '8px 16px',
                      background: 'var(--lex-primary)',
                      color: 'var(--lex-on-primary)',
                      border: 'none',
                      borderRadius: 12,
                      cursor: creating ? 'wait' : 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    {creating ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showArticleIndex && (
            <div
              className="lex-overlay"
              style={{ zIndex: 40, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
              onClick={() => { setShowArticleIndex(false); setIndexSearch(''); }}
            >
              <div
                className="lex-sheet-bottom"
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ padding: 16, borderBottom: '1px solid var(--lex-outline)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--lex-on-surface)' }}>Índice de Artículos</h2>
                    <button type="button" onClick={() => { setShowArticleIndex(false); setIndexSearch(''); }} style={{ padding: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 24, color: 'var(--lex-on-surface-variant)' }} aria-label="Cerrar">
                      <i className="ri-close-line" aria-hidden />
                    </button>
                  </div>
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <i className="ri-search-line" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: 'var(--lex-on-surface-variant)', pointerEvents: 'none' }} aria-hidden />
                    <input
                      type="search"
                      placeholder="Buscar artículo..."
                      value={indexSearch}
                      onChange={(e) => setIndexSearch(e.target.value)}
                      className="lex-input has-icon"
                      style={{ paddingLeft: 40 }}
                    />
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--lex-on-surface-variant)' }}>{indexFiltered.length} artículos</p>
                </div>
                <div style={{ overflowY: 'auto', flex: 1, padding: 8 }}>
                  {indexFiltered.length === 0 ? (
                    <p style={{ color: 'var(--lex-on-surface-variant)', fontSize: 14, padding: 24, textAlign: 'center' }}>No hay coincidencias.</p>
                  ) : (
                    indexFiltered.map((art, idx) => {
                      const color = INDEX_COLORS[idx % INDEX_COLORS.length];
                      const snippet = art.content.replace(/<[^>]+>/g, ' ').trim();
                      const short = snippet.length > 60 ? `${snippet.slice(0, 60)}…` : snippet;
                      return (
                        <button
                          key={art.id}
                          type="button"
                          onClick={() => { setShowArticleIndex(false); setIndexSearch(''); navigate(`/leyes/${lawId}/articulo/${art.id}`); }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: 12,
                            marginBottom: 6,
                            border: 'none',
                            borderRadius: 'var(--lex-radius-sm)',
                            background: art.id === articleId ? 'rgba(25,118,210,0.1)' : 'var(--lex-surface)',
                            cursor: 'pointer',
                            textAlign: 'left',
                            boxShadow: 'var(--lex-card-shadow)',
                          }}
                        >
                          <div style={{ width: 40, height: 40, borderRadius: '50%', background: color, color: 'var(--lex-on-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                            {art.number}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: art.id === articleId ? 'var(--lex-primary)' : 'var(--lex-on-surface)' }}>{art.title || `Artículo ${art.number}`}</div>
                            <div style={{ fontSize: 12, color: 'var(--lex-on-surface-variant)', marginTop: 2 }}>{short}</div>
                          </div>
                          <i className="ri-arrow-right-s-line" style={{ fontSize: 20, color: 'var(--lex-on-surface-variant)', flexShrink: 0 }} aria-hidden />
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </main>
  );
}
